import React, { useEffect, useState } from "react";
import axios from "axios";
import { Plus, Droplets, UtensilsCrossed, Sparkles, ShieldCheck } from "lucide-react";
import { useCart, useLang } from "../store";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PET_TINT = {
  "pet-station-pink": "#FCE7EB",
  "pet-station-grey": "#E8EAEC",
  "pet-station-white": "#F2F1ED",
  "pet-station-green": "#E5EFE0",
};

export default function PetSection() {
  const { lang } = useLang();
  const isEs = lang === "es";
  const { addToCart } = useCart();
  const [products, setProducts] = useState([]);
  const [active, setActive] = useState(0);

  useEffect(() => {
    axios.get(`${API}/products`).then((r) => {
      const all = r.data.products || [];
      setProducts(all.filter((p) => p.category === "pet"));
    });
  }, []);

  if (!products.length) return null;

  const p = products[active];
  const tint = PET_TINT[p.id] || "#F2F1ED";

  const features = [
    { Icon: Droplets, label_es: "Bebedero 1.5L", label_en: "1.5L Water" },
    { Icon: UtensilsCrossed, label_es: "Comedero 1.2L", label_en: "1.2L Food" },
    { Icon: Sparkles, label_es: "Sin BPA", label_en: "BPA-Free" },
    { Icon: ShieldCheck, label_es: "Antivuelco", label_en: "Anti-tip" },
  ];

  return (
    <section
      id="pets"
      className="border-b-[3px] border-[#0C0A09]"
      style={{ background: tint, transition: "background 400ms ease" }}
      data-testid="pet-section"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-10 py-20 md:py-28">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-12">
          <div>
            <span className="nb-badge mb-4">
              {isEs ? "🐾 Nuevo · Para mascotas" : "🐾 New · For pets"}
            </span>
            <h2 className="font-display text-5xl md:text-7xl leading-none tracking-tight mt-3">
              {isEs ? "Velora Pet Station" : "Velora Pet Station"}
            </h2>
          </div>
          <p className="max-w-md text-lg font-medium">
            {isEs
              ? "Bebedero + comedero 2-en-1. Tu mascota nunca se queda sin agua ni comida, aunque salgas todo el día."
              : "Water + food dispenser 2-in-1. Your pet never runs out, even when you're away all day."}
          </p>
        </div>

        {/* Main product + thumbs */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-start">
          {/* Image */}
          <div className="lg:col-span-7">
            <div className="relative bg-white border-[3px] border-[#0C0A09] shadow-[12px_12px_0_0_#0C0A09] overflow-hidden">
              <img
                src={p.image}
                alt={isEs ? p.name_es : p.name_en}
                className="w-full h-[420px] md:h-[560px] object-cover"
                data-testid={`pet-img-${p.slug}`}
              />
              <div className="absolute top-4 left-4 bg-[#FF3D7F] text-white border-[3px] border-[#0C0A09] px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em]">
                -30%
              </div>
              <div className="absolute bottom-4 right-4 bg-[#9BE800] border-[3px] border-[#0C0A09] px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em]">
                {isEs ? "🐶🐱 Perros & Gatos" : "🐶🐱 Dogs & Cats"}
              </div>
            </div>
          </div>

          {/* Buy box */}
          <div className="lg:col-span-5">
            <div className="bg-white border-[3px] border-[#0C0A09] shadow-[8px_8px_0_0_#0C0A09] p-6 md:p-8">
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] opacity-70">
                {isEs ? "Bebedero + Comedero" : "Water + Food"}
              </div>
              <div className="font-display text-4xl md:text-5xl leading-none mt-2">
                {isEs ? p.name_es : p.name_en}
              </div>

              <div className="mt-5 flex items-baseline gap-3">
                <span className="font-display text-5xl">€{p.price.toFixed(2)}</span>
                {p.original_price && p.original_price > p.price && (
                  <>
                    <span className="font-display text-2xl line-through opacity-50">
                      €{p.original_price.toFixed(0)}
                    </span>
                    <span className="px-2 py-1 bg-[#FF3D7F] text-white border-[2px] border-[#0C0A09] rounded-full text-[10px] font-bold uppercase tracking-widest">
                      -30%
                    </span>
                  </>
                )}
              </div>

              <p className="mt-4 text-sm font-medium leading-relaxed opacity-80">
                {isEs
                  ? "Diseño nórdico que combina con tu hogar. Capacidad XL para mantener a tu mascota alimentada e hidratada hasta 7 días. Tapa hermética: comida fresca, sin polvo ni insectos."
                  : "Nordic design that fits any home. XL capacity keeps your pet fed and hydrated for up to 7 days. Airtight lid: fresh food, no dust or bugs."}
              </p>

              {/* Color picker */}
              <div className="mt-6">
                <div className="text-xs font-bold uppercase tracking-[0.2em] opacity-70 mb-3">
                  {isEs ? "Colores" : "Colors"}
                </div>
                <div className="flex flex-wrap gap-3">
                  {products.map((prod, i) => (
                    <button
                      key={prod.id}
                      data-testid={`pet-swatch-${prod.slug}`}
                      onClick={() => setActive(i)}
                      className={`w-12 h-12 rounded-full border-[3px] border-[#0C0A09] transition-transform ${
                        i === active ? "scale-110 shadow-[3px_3px_0_0_#0C0A09]" : "hover:scale-105"
                      }`}
                      style={{ background: prod.hex }}
                      aria-label={isEs ? prod.name_es : prod.name_en}
                    />
                  ))}
                </div>
              </div>

              {/* Scarcity */}
              <div className="mt-5 flex items-center gap-2 bg-[#FF3D7F]/10 border-[3px] border-[#FF3D7F] px-3 py-2">
                <div className="w-2 h-2 rounded-full bg-[#FF3D7F] animate-pulse" />
                <div className="text-xs font-bold uppercase tracking-[0.18em] text-[#FF3D7F]">
                  {isEs ? "⚡ ¡Quedan pocas unidades!" : "⚡ Few units left!"}
                </div>
              </div>

              <button
                data-testid={`add-to-cart-${p.slug}`}
                onClick={() => addToCart(p)}
                className="nb-btn mt-6 w-full justify-center"
              >
                <Plus size={18} strokeWidth={3} /> {isEs ? `Agregar — €${p.price.toFixed(2)}` : `Add to cart — €${p.price.toFixed(2)}`}
              </button>

              {/* Features grid */}
              <div className="mt-6 grid grid-cols-2 gap-2">
                {features.map(({ Icon, label_es, label_en }) => (
                  <div
                    key={label_en}
                    className="flex items-center gap-2 bg-[#FAF9F6] border-[2px] border-[#0C0A09] px-3 py-2"
                  >
                    <Icon size={16} strokeWidth={2.5} />
                    <span className="text-[11px] font-bold uppercase tracking-wider">
                      {isEs ? label_es : label_en}
                    </span>
                  </div>
                ))}
              </div>

              {/* Trust signals row */}
              <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                <div className="text-[10px] font-bold uppercase tracking-[0.15em] py-2 border-[2px] border-[#0C0A09] bg-[#FAF9F6]">
                  {isEs ? "📦 Envío 24h" : "📦 24h Ship"}
                </div>
                <div className="text-[10px] font-bold uppercase tracking-[0.15em] py-2 border-[2px] border-[#0C0A09] bg-[#FAF9F6]">
                  {isEs ? "🔒 Pago seguro" : "🔒 Secure pay"}
                </div>
                <div className="text-[10px] font-bold uppercase tracking-[0.15em] py-2 border-[2px] border-[#0C0A09] bg-[#FAF9F6]">
                  {isEs ? "↩️ Devolución 30d" : "↩️ 30d Return"}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Photo gallery */}
        <div className="mt-16">
          <div className="flex items-end justify-between mb-6">
            <h3 className="font-display text-3xl md:text-4xl leading-none">
              {isEs ? "Galería del producto" : "Product gallery"}
            </h3>
            <span className="nb-badge">
              {isEs ? "📸 4 colores disponibles" : "📸 4 colors available"}
            </span>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
            {[
              "/pets/pet-lifestyle-dog-cat.jpg",
              "/pets/pet-lifestyle-cat.jpg",
              "/pets/pet-family-4colors.jpg",
              "/pets/pet-duo-clean.jpg",
              "/pets/pet-grey-clean.jpg",
            ].map((src, idx) => (
              <div
                key={src}
                className="aspect-square bg-white border-[3px] border-[#0C0A09] shadow-[4px_4px_0_0_#0C0A09] overflow-hidden hover:shadow-[6px_6px_0_0_#0C0A09] hover:-translate-y-0.5 transition-all"
                data-testid={`pet-gallery-${idx}`}
              >
                <img src={src} alt={`Velora Pet Station ${idx + 1}`} className="w-full h-full object-cover" loading="lazy" />
              </div>
            ))}
          </div>
        </div>

        {/* Use cases banner */}
        <div className="mt-16 bg-[#0C0A09] text-white border-[3px] border-[#0C0A09] p-6 md:p-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-10">
            <div>
              <div className="text-4xl mb-2">🐶</div>
              <div className="font-display text-2xl mb-2">{isEs ? "Para perros" : "For dogs"}</div>
              <div className="text-sm font-medium opacity-80">
                {isEs
                  ? "Razas pequeñas y medianas. Capacidad para 5-7 días."
                  : "Small and medium breeds. 5-7 days capacity."}
              </div>
            </div>
            <div>
              <div className="text-4xl mb-2">🐱</div>
              <div className="font-display text-2xl mb-2">{isEs ? "Para gatos" : "For cats"}</div>
              <div className="text-sm font-medium opacity-80">
                {isEs
                  ? "Perfecto para 1-2 gatos. Tapa hermética: comida siempre fresca."
                  : "Perfect for 1-2 cats. Airtight lid keeps food fresh."}
              </div>
            </div>
            <div>
              <div className="text-4xl mb-2">✈️</div>
              <div className="font-display text-2xl mb-2">{isEs ? "Cuando viajas" : "When traveling"}</div>
              <div className="text-sm font-medium opacity-80">
                {isEs
                  ? "Te vas un fin de semana? Tu mascota queda bien atendida."
                  : "Going for the weekend? Your pet stays well-fed."}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
